import { fastagAPI } from "../utils/apiConfig";
import { getCityFromPlate } from "../utils/rtoData";

// State code → full state name for precise Nominatim queries
const STATE_NAMES = {
  AN:"Andaman and Nicobar", AP:"Andhra Pradesh", AR:"Arunachal Pradesh",
  AS:"Assam",   BR:"Bihar",      CG:"Chhattisgarh", CH:"Chandigarh",
  DD:"Daman and Diu", DL:"Delhi", GA:"Goa",        GJ:"Gujarat",
  HP:"Himachal Pradesh", HR:"Haryana", JH:"Jharkhand", JK:"Jammu and Kashmir",
  KA:"Karnataka", KL:"Kerala",  LA:"Ladakh",        MH:"Maharashtra",
  ML:"Meghalaya", MN:"Manipur", MP:"Madhya Pradesh", MZ:"Mizoram",
  NL:"Nagaland",  OD:"Odisha",  OR:"Odisha",         PB:"Punjab",
  PY:"Puducherry", RJ:"Rajasthan", SK:"Sikkim",      TG:"Telangana",
  TN:"Tamil Nadu", TR:"Tripura",  TS:"Telangana",    UK:"Uttarakhand",
  UP:"Uttar Pradesh", UT:"Uttarakhand", WB:"West Bengal",
};

// Hardcoded state capital coordinates — guaranteed fallback when Nominatim is unavailable
const STATE_COORDS = {
  AN:{ lat:11.6234, lng:92.7265,  city:"Port Blair"       },
  AP:{ lat:16.5062, lng:80.6480,  city:"Vijayawada"       },
  AR:{ lat:27.0844, lng:93.6053,  city:"Itanagar"         },
  AS:{ lat:26.1445, lng:91.7362,  city:"Guwahati"         },
  BR:{ lat:25.5941, lng:85.1376,  city:"Patna"            },
  CG:{ lat:21.2514, lng:81.6296,  city:"Raipur"           },
  CH:{ lat:30.7333, lng:76.7794,  city:"Chandigarh"       },
  DD:{ lat:20.3974, lng:72.8328,  city:"Daman"            },
  DL:{ lat:28.7041, lng:77.1025,  city:"Delhi"            },
  GA:{ lat:15.4909, lng:73.8278,  city:"Panaji"           },
  GJ:{ lat:23.0225, lng:72.5714,  city:"Ahmedabad"        },
  HP:{ lat:31.1048, lng:77.1734,  city:"Shimla"           },
  HR:{ lat:28.4595, lng:77.0266,  city:"Gurugram"         },
  JH:{ lat:23.3441, lng:85.3096,  city:"Ranchi"           },
  JK:{ lat:34.0837, lng:74.7973,  city:"Srinagar"         },
  KA:{ lat:12.9716, lng:77.5946,  city:"Bengaluru"        },
  KL:{ lat:8.5241,  lng:76.9366,  city:"Thiruvananthapuram"},
  LA:{ lat:34.1526, lng:77.5771,  city:"Leh"              },
  MH:{ lat:19.0760, lng:72.8777,  city:"Mumbai"           },
  ML:{ lat:25.5788, lng:91.8933,  city:"Shillong"         },
  MN:{ lat:24.8170, lng:93.9368,  city:"Imphal"           },
  MP:{ lat:23.2599, lng:77.4126,  city:"Bhopal"           },
  MZ:{ lat:23.1645, lng:92.9376,  city:"Aizawl"           },
  NL:{ lat:25.6751, lng:94.1086,  city:"Kohima"           },
  OD:{ lat:20.2961, lng:85.8245,  city:"Bhubaneswar"      },
  OR:{ lat:20.2961, lng:85.8245,  city:"Bhubaneswar"      },
  PB:{ lat:30.9010, lng:75.8573,  city:"Ludhiana"         },
  PY:{ lat:11.9416, lng:79.8083,  city:"Pondicherry"      },
  RJ:{ lat:26.9124, lng:75.7873,  city:"Jaipur"           },
  SK:{ lat:27.3389, lng:88.6065,  city:"Gangtok"          },
  TG:{ lat:17.3850, lng:78.4867,  city:"Hyderabad"        },
  TN:{ lat:13.0827, lng:80.2707,  city:"Chennai"          },
  TR:{ lat:23.9408, lng:91.9882,  city:"Agartala"         },
  TS:{ lat:17.3850, lng:78.4867,  city:"Hyderabad"        },
  UK:{ lat:30.3165, lng:78.0322,  city:"Dehradun"         },
  UP:{ lat:26.8467, lng:80.9462,  city:"Lucknow"          },
  UT:{ lat:30.3165, lng:78.0322,  city:"Dehradun"         },
  WB:{ lat:22.5726, lng:88.3639,  city:"Kolkata"          },
};

// ── Nominatim geocoder ────────────────────────────────────────────────────────
async function geocodeCity(city, query) {
  try {
    const q = query || `${city}, India`;
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`;
    const res = await fetch(url, { headers: { "User-Agent": "TrackinApp/1.0" } });
    const data = await res.json();
    if (!data?.length) return null;
    return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), city };
  } catch {
    return null;
  }
}

// Geocode a vehicle's location from its plate number.
// Priority: 1) Nominatim with state-qualified query  2) Nominatim city-only
// 3) Hardcoded state capital coords — always returns something for Indian plates.
async function geocodeStateFromPlate(plateNumber) {
  const city      = getCityFromPlate(plateNumber);
  const stateCode = plateNumber.replace(/[\s-]/g, "").toUpperCase().slice(0, 2);
  const stateName = STATE_NAMES[stateCode] || "";

  if (city) {
    // Try precise state-qualified query first
    const query = stateName ? `${city}, ${stateName}, India` : `${city}, India`;
    const result = await geocodeCity(city, query);
    if (result) return { ...result, stateCode, city };

    // Retry with city name only (avoids Nominatim quirks with state qualifiers)
    const retry = await geocodeCity(city);
    if (retry) return { ...retry, stateCode, city };
  }

  // Guaranteed fallback — hardcoded state capital coordinates
  const fallback = STATE_COORDS[stateCode];
  if (fallback) return { ...fallback, stateCode };

  return null;
}

// ── Vehicle type code → readable label ───────────────────────────────────────
const VC_TYPE_MAP = {
  VC10: "Truck",   // Heavy commercial — confirmed from live API
  VC11: "Two-Wheeler",
  VC21: "Car",
  VC31: "Car",
  VC41: "Truck",
  VC42: "Bus",
  VC51: "Truck",
  VC61: "Truck",
  VC71: "Truck",
  VC81: "Truck",
  VC91: "Truck",
};

function vcToType(vcCode) {
  if (!vcCode) return "Other";
  return VC_TYPE_MAP[vcCode.toUpperCase()] || "Other";
}

function parseGeocode(geocodeStr) {
  if (!geocodeStr) return null;
  const parts = geocodeStr.split(",");
  if (parts.length < 2) return null;
  const lat = parseFloat(parts[0].trim());
  const lng = parseFloat(parts[1].trim());
  if (isNaN(lat) || isNaN(lng)) return null;
  return { lat, lng };
}

// Masters India FASTag API — CONFIRMED live response structure:
//  payload.response[0].response.vehicle.vehltxnList.txn[]
//  NOTE: field is "vehltxnList" (lowercase L), NOT "vehItxnList" (capital I)
//  CRITICAL: when only 1 transaction exists, API returns txn as an OBJECT (not array).
//  Always normalise to array.
function extractTransactions(data) {
  try {
    const outer = data?.response?.[0];
    if (!outer) return null;

    const inner = outer?.response;
    if (!inner) return null;

    // When result is FAILED AND errCode is present — no data available
    const errCode = inner?.vehicle?.errCode;
    if (inner?.result === "FAILED" && errCode && errCode !== "000") return null;

    const raw = inner?.vehicle?.vehltxnList?.txn;
    if (!raw) return null;

    // ← THE BUG: single transaction arrives as an object, not an array
    const txList = Array.isArray(raw) ? raw : [raw];
    if (txList.length === 0) return null;

    // Filter out entries that have no readable timestamp
    const valid = txList.filter(t => t && (t.readerReadTime || t.tollPlazaName));
    if (valid.length === 0) return null;

    return { txList: valid, vehicleType: valid[0]?.vehicleType || "" };
  } catch {
    return null;
  }
}

// Build YYYY-MM-DD date string offset by N days from today
function dateOffset(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function mapApiResponse(plateNumber, data) {
  const extracted = extractTransactions(data);
  if (!extracted) return null;

  const { txList, vehicleType } = extracted;

  const sorted = [...txList].sort(
    (a, b) => new Date(a.readerReadTime) - new Date(b.readerReadTime)
  );

  const tollHistory = [...sorted].reverse().map((tx, i) => {
    const geo = parseGeocode(tx.tollPlazaGeocode);
    return {
      id:        `live_${i}_${tx.seqNo || i}`,
      plaza:     tx.tollPlazaName || "Toll Plaza",
      timestamp: tx.readerReadTime
        ? new Date(tx.readerReadTime).toISOString()
        : new Date().toISOString(),
      direction: tx.laneDirection || "",    // raw NETC code: E=Entry X=Exit N/S/W=compass
      location:  tx.laneDirection === "E" ? "Entry" : tx.laneDirection === "X" ? "Exit" : tx.laneDirection || "",
      lat:       geo?.lat ?? null,
      lng:       geo?.lng ?? null,
    };
  });

  const route = sorted
    .map(tx => parseGeocode(tx.tollPlazaGeocode))
    .filter(Boolean);

  const latest    = sorted[sorted.length - 1];
  // Use latest geocode, or fall back to any geocode in the list
  const latestGeo = parseGeocode(latest.tollPlazaGeocode)
    || route[route.length - 1]
    || { lat: 20.5937, lng: 78.9629 }; // India center as absolute last resort

  return {
    plateNumber,
    type:          vcToType(vehicleType),
    lat:           latestGeo.lat,
    lng:           latestGeo.lng,
    location:      latest.tollPlazaName || "Last Known Toll",
    startLocation: sorted[0]?.tollPlazaName || "Journey Start",
    lastUpdated:   latest.readerReadTime
      ? new Date(latest.readerReadTime).toISOString()
      : new Date().toISOString(),
    tollHistory,
    route: route.length > 0 ? route : [latestGeo],
    verified: true,
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useFastagAPI() {

  // Verify + get initial snapshot for a new vehicle plate.
  // Throws only on real API errors (network failure, auth, quota).
  // "Data Not Found" from Masters India = no FASTag registered → returns noRecentData: true.
  async function verifyVehicle(plateNumber) {
    const normalized = plateNumber.replace(/[\s-]/g, "").toUpperCase();
    const body = {
      vehiclenumber: normalized,
      fromdate: dateOffset(-60),
      todate:   dateOffset(0),
    };

    const noDataResult = {
      plateNumber,
      verified:      false,
      noRecentData:  true,
      lat:           20.5937,
      lng:           78.9629,
      location:      "No FASTag crossings in last 60 days",
      startLocation: "Unknown",
      lastUpdated:   new Date().toISOString(),
      tollHistory:   [],
      route:         [],
      type:          "Other",
    };

    let res;
    try {
      res = await fastagAPI.post("/FASTAG/", body);
    } catch (err) {
      // Masters India returns 404 when the plate has no FASTag registration.
      // The backend forwards it as a 404 with { error: "Data Not Found" }.
      // Treat this as noRecentData instead of a hard failure.
      const isNotFound =
        err.status === 404 ||
        String(err.message || "").toLowerCase().includes("not found") ||
        String(err.message || "").toLowerCase().includes("no data");
      if (isNotFound) return noDataResult;
      throw err;
    }

    const data = res.data;
    // Masters India sometimes wraps response under data.data
    const payload = data?.data ?? data;

    if (payload?.message === "Success" && payload?.code === "200") {
      // Always try to extract real data first — even on "FAILED" result
      // (API may say FAILED but still include transaction data for the period)
      const mapped = mapApiResponse(plateNumber, payload);
      if (mapped) return mapped;

      // No transaction data parsed — genuine "no data" case
      const errCode = payload?.response?.[0]?.response?.vehicle?.errCode;
      return { ...noDataResult, errCode };
    }

    // JSON-level "not found" (HTTP 200 but Masters India signals no data in the body)
    const bodyMsg = String(payload?.message || data?.message || "").toLowerCase();
    if (bodyMsg.includes("not found") || bodyMsg.includes("no data")) {
      return noDataResult;
    }

    throw new Error(data?.message || "FASTag lookup failed");
  }

  // Fetch latest data for a tracked vehicle (called every 3s poll).
  // Returns null when no new data — caller keeps last known position.
  async function fetchVehicleData(plateNumber) {
    try {
      const normalized = plateNumber.replace(/[\s-]/g, "").toUpperCase();
      const res = await fastagAPI.post("/FASTAG/", {
        vehiclenumber: normalized,
        fromdate: dateOffset(-60),
        todate:   dateOffset(0),
      });
      const payload = res.data?.data ?? res.data;
      return mapApiResponse(plateNumber, payload) || null;
    } catch {
      return null;
    }
  }

  async function fetchTollHistory(plateNumber) {
    try {
      const normalized = plateNumber.replace(/[\s-]/g, "").toUpperCase();
      const res = await fastagAPI.post("/FASTAG/", {
        vehiclenumber: normalized,
        fromdate: dateOffset(-60),
        todate:   dateOffset(0),
      });
      const payload = res.data?.data ?? res.data;
      const mapped  = mapApiResponse(plateNumber, payload);
      return mapped?.tollHistory || [];
    } catch {
      return [];
    }
  }

  return { verifyVehicle, fetchVehicleData, fetchTollHistory, geocodeCity, geocodeStateFromPlate };
}
